import React, { useState, useEffect } from "react";
import useApi from "../../../hooks/useApi";

import Button from "../../../atoms/Button/Button";
import PageHeader from "../../../molecules/PatientDetailsV2/PageHeader/PageHeader";
import RecordsPagination from "./RecordsPagination";
import RecordsFilter from "./RecordsFilter";
import Popup from "../../../atoms/Popup/Popup";
import UploadRecordModal from "./UploadRecordModal";

import API from "../../../services/rest/api";
import LOCAL_STORAGE from "../../../constants/localStorage";
import { truncate } from "../../../utils/common";

import "./RecordsPage.scss";
import { TABLE } from "../../../constants/common";
import { formatDateTime } from "../../../utils/date-and-time";

import { ReactComponent as RecordIcon } from "../../../assets/icons/patient-layout-v2/records/RecordIcon.svg";
import { ReactComponent as ArrowRightIcon } from "../../../assets/icons/patient-layout-v2/overview/ArrowRightIcon.svg";
import { ReloadOutlined } from "@ant-design/icons";

const SKELETON_ROWS = [1, 2, 3, 4];

const RecordsPage: React.FC<any> = () => {
  const {
    mutate: getRecords,
    isLoading,
    response: recordsResponse,
    error: recordsError,
  } = useApi(API.GetRecords);

  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(TABLE.DEFAULT_PAGINATION);
  const [recordType, setRecordType] = useState("");

  const [showUploadRecordModal, setShowUploadRecordModal] = useState(false);

  const patientId = localStorage.getItem(LOCAL_STORAGE.PATIENT_ID);

  useEffect(() => {
    fetchRecords();
  }, [patientId, currentPage, pageSize, recordType]); // Fetch records when currentPage or pageSize changes

  useEffect(() => {
    if (Number(recordsResponse?.code || 0) === 1) {
      setRecords(recordsResponse.data);
      setTotalRecords(recordsResponse.total_data || 0);
    } else if (recordsResponse || recordsError) {
      setRecords([]);
      setTotalRecords(0);
    }
  }, [recordsResponse, recordsError]);

  const resetData = () => {
    setCurrentPage(1);
    setPageSize(TABLE.DEFAULT_PAGINATION);
    setRecordType("");
  };

  const fetchRecords = (
    overrides: Partial<{ page: number; per_page: number; filter: string }> = {},
  ) => {
    const payload = {
      page: currentPage,
      per_page: pageSize,
      patient_id: patientId,
      filter: recordType,
      ...overrides,
    };
    getRecords(payload);
  };

  const refresh = () => {
    resetData();
    // resetData() is a no-op when already on defaults, which would leave the
    // fetch-on-change effect below untriggered — fetch explicitly instead.
    fetchRecords({ page: 1, per_page: TABLE.DEFAULT_PAGINATION, filter: "" });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size); // Set the new page size
    setCurrentPage(1); // Reset to the first page when page size changes
  };

  const handleRecordTypeChange = (type: string) => {
    setRecordType(type);
    setCurrentPage(1); // Reset page
  };

  const handleUploadSuccess = () => {
    resetData();
    fetchRecords({ page: 1, per_page: TABLE.DEFAULT_PAGINATION, filter: "" });
    setShowUploadRecordModal(false);
  };

  const getDocumentUrl = (row: any) =>
    Array.isArray(row.document_url) ? row.document_url[0] : row.document_url;

  return (
    <div className="records-page-container">
      <PageHeader
        title="Patient Records"
        actions={
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={refresh}
              title="Refresh"
              loading={isLoading}
              iconStart={!isLoading && <ReloadOutlined />}
            />
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setShowUploadRecordModal(true)}
            >
              + Upload Record
            </Button>
          </>
        }
      />

      <RecordsFilter selected={recordType} onChange={handleRecordTypeChange} />

      <div className="records-cards">
        {isLoading &&
          SKELETON_ROWS.map((row) => (
            <div className="record-card record-card-skeleton" key={row}>
              <div className="skeleton-block skeleton-ico" />
              <div className="record-card-main">
                <div className="skeleton-block skeleton-line skeleton-line-name" />
                <div className="skeleton-block skeleton-line skeleton-line-meta" />
              </div>
              <div className="skeleton-block skeleton-line skeleton-line-btn" />
            </div>
          ))}

        {!isLoading && records?.length === 0 && (
          <div className="records-cards-empty">No Records found!</div>
        )}

        {!isLoading &&
          records?.map((row: any, rowIndex: number) => (
            <div className="record-card" key={row.id ?? rowIndex}>
              <div className="record-card-ico">
                <RecordIcon />
              </div>
              <div className="record-card-main">
                <div className="record-card-name" title={row.name}>
                  {truncate(row.name)}
                </div>
                <div className="record-card-meta">
                  {row.label}
                  <span className="sep">·</span>
                  {formatDateTime({
                    timestamp: row.created_at,
                    format: 12,
                    showTime: true,
                  })}
                </div>
              </div>
              <a
                className="record-card-view"
                href={getDocumentUrl(row)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Document
                <ArrowRightIcon />
              </a>
            </div>
          ))}
      </div>

      {!isLoading && records?.length > 0 && (
        <RecordsPagination
          totalRecords={totalRecords}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          dataLength={records?.length}
        />
      )}

      {showUploadRecordModal && (
        <Popup
          show={showUploadRecordModal}
          headerText="Upload Record"
          onClose={() => setShowUploadRecordModal(false)}
        >
          <UploadRecordModal
            onSuccess={handleUploadSuccess}
            onCancel={() => setShowUploadRecordModal(false)}
          />
        </Popup>
      )}
    </div>
  );
};

export default RecordsPage;
